import { z } from "zod";
import { normalizeBuildProgress } from "../domain/build/buildProgress";
/**
 * Repository for CRUD operations on the designs table.
 * All operations are scoped to the authenticated user via RLS.
 */
import { normalizeFinishes } from "../domain/finishes/types";
import { normalizeWallHeightM } from "../domain/geometry/buildingDimensions";
import { normalizeStoredIndoorRoom } from "../domain/indoor/indoorRoom";
import { normalizeInteriorLayout } from "../domain/interior/normalizeInteriorLayout";
import { normalizeClimateProfile } from "../domain/location/types";
import { normalizeRoofConfig } from "../domain/roof/roofConfig";
import { snapRotationDeg } from "../domain/viewer/saunaRotation";
import { normalizeTimeOfDay } from "../domain/viewer/timeOfDay";
import { supabase } from "../lib/supabase";
import { DEFAULT_SITE_CONDITIONS } from "../pages/builder/constants";
import type { Design, DesignInsert, DesignUpdate } from "../types/design";

const TABLE = "designs";

// Sentinel thrown by write operations when the backend is not configured. UI
// callers map this to the localized degraded notice; never shown verbatim.
export const BACKEND_UNAVAILABLE = "backend-unavailable";

/** Narrow the nullable client for a write path, or throw the sentinel. */
function requireClient(): NonNullable<typeof supabase> {
	if (!supabase) throw new Error(BACKEND_UNAVAILABLE);
	return supabase;
}

type DesignDbRow = Omit<
	Design,
	| "thermalInputs"
	| "siteConditions"
	| "roofConfig"
	| "buildProgress"
	| "locationMode"
	| "postalCode"
	| "climateProfile"
	| "entryWall"
	| "glazingStyle"
	| "saunaPosition"
	| "hasRelaxRoom"
	| "timeOfDay"
	| "rotationDeg"
	| "wizardCapacityHint"
	| "interiorLayout"
	| "finishes"
	| "wallHeightM"
	| "indoorRoom"
> & {
	location_mode?: Design["locationMode"];
	postal_code?: Design["postalCode"];
	climate_profile?: Design["climateProfile"];
	partition_split?: Design["partitionSplit"];
	entry_wall?: Design["entryWall"] | null;
	glazing_style?: Design["glazingStyle"] | null;
	sauna_position?: Design["saunaPosition"] | null;
	has_relax_room?: Design["hasRelaxRoom"] | null;
	time_of_day?: Design["timeOfDay"] | string | null;
	rotation_deg?: number | null;
	wizard_capacity_hint?: number | null;
	roof_config: Design["roofConfig"];
	deck_config?: Design["deckConfig"];
	thermal_inputs: Design["thermalInputs"];
	site_conditions: Design["siteConditions"];
	build_progress?: Design["buildProgress"];
	interior_layout?: Design["interiorLayout"] | null;
	finishes?: Design["finishes"] | null;
	wall_height_m?: number | null;
	indoor_room?: unknown;
};

// ─── DB row validation ──────────────────────────────────────
//
// Supabase rows carry jsonb columns we never validated — a corrupt row
// (stale schema, partial write, hand-edited admin row) used to flow
// into Design and then into engines, where it would crash on a
// downstream field access. The schema below catches structural drift
// at the boundary. Nested jsonb objects are validated as `object` only;
// the existing normalizers (normalizeRoofConfig, DEFAULT_SITE_CONDITIONS
// spread) handle malformed internals.

const DesignDbRowSchema = z
	.object({
		id: z.string(),
		user_id: z.string(),
		name: z.string(),
		type: z.enum(["fyrkantig", "tunna", "tvarum", "inomhus"]),
		market: z.enum(["se", "intl"]).optional(),
		location_mode: z.enum(["postal_code", "manual_climate"]).optional(),
		postal_code: z.string().optional().nullable(),
		climate_profile: z.unknown().optional().nullable(),
		width: z.number(),
		length: z.number(),
		partition_split: z.number().optional().nullable(),
		entry_wall: z.enum(["short", "long"]).optional().nullable(),
		glazing_style: z.enum(["standard", "panorama"]).optional().nullable(),
		sauna_position: z.enum(["left", "right"]).optional().nullable(),
		has_relax_room: z.boolean().optional().nullable(),
		time_of_day: z
			.union([z.number(), z.enum(["day", "evening"])])
			.optional()
			.nullable(),
		rotation_deg: z.number().optional().nullable(),
		wizard_capacity_hint: z.number().optional().nullable(),
		visibility: z.object({}).passthrough().optional(),
		openings: z.array(z.unknown()).optional().nullable(),
		deck_config: z.object({}).passthrough().nullable().optional(),
		roof_config: z.unknown().optional().nullable(),
		build_progress: z
			.record(
				z.string(),
				z
					.object({
						completed: z.boolean().optional(),
						completedCheckpointIds: z.array(z.string()).optional(),
						completedSubstepIds: z.array(z.string()).optional(),
					})
					.passthrough(),
			)
			.optional()
			.nullable(),
		thermal_inputs: z.object({}).passthrough().optional().nullable(),
		site_conditions: z.object({}).passthrough().optional().nullable(),
		// Accepted loosely here and normalized in mapDbToDesign: the column has
		// no DB-side shape constraint (a stored layout is meant to be repaired
		// against the current room, not rejected), so a malformed document must
		// degrade to "no layout" rather than fail the whole row.
		interior_layout: z.unknown().optional().nullable(),
		finishes: z.unknown().optional().nullable(),
		wall_height_m: z.number().optional().nullable(),
		// Loose like `finishes`: normalized in mapDbToDesign, garbage degrades
		// to "free-standing, height not given" rather than failing the row.
		indoor_room: z.unknown().optional().nullable(),
		created_at: z.string(),
		updated_at: z.string(),
	})
	.passthrough();

const DESIGN_INPUT_FIELD_MAP = [
	["roofConfig", "roof_config"],
	["deckConfig", "deck_config"],
	["partitionSplit", "partition_split"],
	["entryWall", "entry_wall"],
	["glazingStyle", "glazing_style"],
	["saunaPosition", "sauna_position"],
	["hasRelaxRoom", "has_relax_room"],
	["timeOfDay", "time_of_day"],
	["rotationDeg", "rotation_deg"],
	["wallHeightM", "wall_height_m"],
	["indoorRoom", "indoor_room"],
	["wizardCapacityHint", "wizard_capacity_hint"],
	["locationMode", "location_mode"],
	["postalCode", "postal_code"],
	["climateProfile", "climate_profile"],
	["buildProgress", "build_progress"],
	["thermalInputs", "thermal_inputs"],
	["siteConditions", "site_conditions"],
	["interiorLayout", "interior_layout"],
] as const;

function parseDbRow(raw: unknown): DesignDbRow {
	const result = DesignDbRowSchema.safeParse(raw);
	if (!result.success) {
		throw new Error(
			`Malformed design row from Supabase: ${result.error.message}`,
		);
	}
	return result.data as unknown as DesignDbRow;
}

function mapDbToDesign(row: DesignDbRow): Design {
	const thermalDefaults = {
		glassAreaSqm: 0,
		stoneAreaSqm: 0,
		isOutdoorLogCabin: false,
		heaterType: "wood" as const,
		includesShower: false,
	};

	return {
		...row,
		market: row.market ?? "se",
		locationMode: row.location_mode ?? "postal_code",
		postalCode: row.postal_code ?? undefined,
		climateProfile: row.climate_profile
			? normalizeClimateProfile(row.climate_profile)
			: undefined,
		openings: row.openings ?? [],
		partitionSplit: row.partition_split ?? 0.5,
		entryWall: row.entry_wall ?? undefined,
		glazingStyle: row.glazing_style ?? undefined,
		saunaPosition: row.sauna_position ?? undefined,
		hasRelaxRoom: row.has_relax_room ?? undefined,
		timeOfDay: normalizeTimeOfDay(row.time_of_day),
		// Rows written before the column existed come back null → 0.
		rotationDeg: snapRotationDeg(row.rotation_deg ?? undefined),
		wizardCapacityHint: row.wizard_capacity_hint ?? null,
		deckConfig: row.deck_config ?? null,
		roofConfig: normalizeRoofConfig(row.type, row.roof_config),
		buildProgress: normalizeBuildProgress(row.build_progress),
		thermalInputs: {
			...thermalDefaults,
			...(row.thermal_inputs ?? {}),
		},
		siteConditions: {
			...DEFAULT_SITE_CONDITIONS,
			...(row.site_conditions ?? {}),
		},
		// Rows written before the column existed come back undefined -> null,
		// the "no layout authored" state the engines generate for. Anything
		// else is structurally validated: a layout missing `benchRuns` used to
		// typecheck its way into the engines and throw there.
		interiorLayout: normalizeInteriorLayout(row.interior_layout),
		// Same story as `interiorLayout`: pre-issue-134 rows come back
		// null/undefined and normalize to the defaults, which are the look those
		// designs were drawn with. An unrecognised value degrades per field.
		finishes: normalizeFinishes(row.finishes),
		// Pre-issue-155 rows come back null: the rule default builds. A stored
		// value goes through the same snap/clamp the engines apply.
		wallHeightM: normalizeWallHeightM(row.wall_height_m),
		// Pre-issue-174 rows and every non-indoor design come back null.
		indoorRoom: normalizeStoredIndoorRoom(row.indoor_room),
	};
}

function mapDesignInput(input: DesignInsert | DesignUpdate) {
	const next: Record<string, unknown> = { ...input };

	for (const [appKey, dbKey] of DESIGN_INPUT_FIELD_MAP) {
		if (appKey in next) {
			next[dbKey] = next[appKey];
			delete next[appKey];
		}
	}

	return next;
}

export async function listDesigns(): Promise<Design[]> {
	if (!supabase) return [];
	const { data, error } = await supabase
		.from(TABLE)
		.select("*")
		.order("updated_at", { ascending: false });

	if (error) throw error;
	const out: Design[] = [];
	for (const raw of data ?? []) {
		const parsed = DesignDbRowSchema.safeParse(raw);
		if (!parsed.success) {
			console.warn(
				"Skipping malformed design row:",
				(raw as { id?: string })?.id ?? "<unknown>",
				parsed.error.message,
			);
			continue;
		}
		out.push(mapDbToDesign(parsed.data as unknown as DesignDbRow));
	}
	return out;
}

export async function getDesign(id: string): Promise<Design | null> {
	if (!supabase) return null;
	const { data, error } = await supabase
		.from(TABLE)
		.select("*")
		.eq("id", id)
		.single();

	if (error) {
		if (error.code === "PGRST116") return null; // Not found
		throw error;
	}
	return mapDbToDesign(parseDbRow(data));
}

export async function createDesign(design: DesignInsert): Promise<Design> {
	const client = requireClient();
	const {
		data: { user },
	} = await client.auth.getUser();
	if (!user) throw new Error("Inte inloggad");

	const { data, error } = await client
		.from(TABLE)
		.insert({ ...mapDesignInput(design), user_id: user.id })
		.select()
		.single();

	if (error) throw error;
	return mapDbToDesign(parseDbRow(data));
}

export async function updateDesign(
	id: string,
	updates: DesignUpdate,
): Promise<Design> {
	const { data, error } = await requireClient()
		.from(TABLE)
		.update(mapDesignInput(updates))
		.eq("id", id)
		.select()
		.single();

	if (error) throw error;
	return mapDbToDesign(parseDbRow(data));
}

export async function deleteDesign(id: string): Promise<void> {
	const { error } = await requireClient().from(TABLE).delete().eq("id", id);

	if (error) throw error;
}
